import * as THREE from "three";
import { SWITCH_LOAD_TEST_NODE_ID } from "../electrical/roomCircuit";
import { roomChallenge } from "../workshop/RoomChallenge";
import type { ControllerHandle, InteractionSystem } from "../xr/input";
import type { XRRig } from "../xr/session";
import type { WorkshopEnvironment } from "../world/room";
import { createGrabbable, type Grabbable } from "./grabbable";
import { GrabManager } from "./GrabManager";
import {
  createPlacementTarget,
  lockIntoTarget,
  type PlacementTarget,
} from "./placement";

/** Probe-to-jack contact radius — training-forgiving, no scene raycast. */
const PROBE_CONTACT_RADIUS = 0.07;
/** Drop-near-dock snap radius. */
const METER_DOCK_SNAP_RADIUS = 0.12;
const IDLE_DISPLAY = "----";

interface TestContact {
  nodeId: string;
  object: THREE.Object3D;
}

export interface MeterSystemOptions {
  rig: XRRig;
  interaction: InteractionSystem;
  env: WorkshopEnvironment;
}

/**
 * Portable one-probe tester: grab the meter body, touch a jack, read voltage.
 *
 * Own {@link GrabManager} (not shared with jumpers) so a drop near a circuit
 * terminal cannot reject the meter as the wrong role. COM is conceptual
 * source — {@link roomChallenge.formatMeterVoltage} at the contacted node.
 */
export class MeterSystem {
  private readonly rig: XRRig;
  private readonly interaction: InteractionSystem;
  private readonly grabs: GrabManager;
  private readonly grabbable: Grabbable;
  private readonly restTarget: PlacementTarget;
  private readonly probeTip: THREE.Object3D;
  private readonly displayTexture: THREE.CanvasTexture;
  private readonly displayCanvas: HTMLCanvasElement;
  private readonly displayCtx: CanvasRenderingContext2D;
  private readonly testContacts: TestContact[];

  private contactNodeId: string | null = null;
  private displayText = IDLE_DISPLAY;

  private readonly _probe = new THREE.Vector3();
  private readonly _jack = new THREE.Vector3();

  constructor(opts: MeterSystemOptions) {
    this.rig = opts.rig;
    this.interaction = opts.interaction;
    const { env } = opts;

    uniquifyStandardMaterials(env.meter);
    uniquifyStandardMaterials(env.meterRest);

    env.meter.updateMatrixWorld(true);
    env.meterRest.updateMatrixWorld(true);
    const snap = relativeLocalPose(env.meterRest, env.meter);

    this.grabbable = createGrabbable({
      id: "meter",
      object: env.meter,
      // Dock uses the body origin, not the probe tip.
      placeAnchor: env.meter,
      unplugOnGrab: true,
    });

    this.restTarget = createPlacementTarget({
      id: "meter-rest",
      object: env.meterRest,
      accepts: "meter",
      snapRadius: METER_DOCK_SNAP_RADIUS,
      snapLocalPosition: snap.position,
      snapLocalQuaternion: snap.quaternion,
    });

    this.grabs = new GrabManager({
      rig: opts.rig,
      interaction: opts.interaction,
      // Sit slightly right of the view so desktop click-to-dock can hit the rest.
      desktopHoldOffset: new THREE.Vector3(0.14, -0.06, -0.42),
      proximityRadius: 0.14,
      onPlaceOutcome: () => this.onNotProbing(),
    });
    this.grabs.addGrabbable(this.grabbable);
    this.grabs.addTarget(this.restTarget);
    lockIntoTarget(this.grabbable, this.restTarget);

    this.probeTip =
      env.meterProbe.getObjectByName("meter-probe-tip") ?? env.meterProbe;
    this.displayTexture = env.meterDisplayTexture;
    const image = env.meterDisplayTexture.image;
    if (!(image instanceof HTMLCanvasElement)) {
      throw new Error("[MeterSystem] meter display texture has no canvas");
    }
    this.displayCanvas = image;
    const ctx = image.getContext("2d");
    if (!ctx) throw new Error("[MeterSystem] meter display canvas has no 2d context");
    this.displayCtx = ctx;

    this.testContacts = collectTestContacts(env);
    this.paintDisplay(IDLE_DISPLAY);
  }

  update(dt: number): void {
    this.grabs.update(dt);

    if (!this.grabs.isHeld(this.grabbable)) {
      this.onNotProbing();
      return;
    }

    const hit = this.nearestContact();
    if (!hit) {
      this.onNotProbing();
      return;
    }

    const text = roomChallenge.formatMeterVoltage(hit.nodeId);
    const entered = this.contactNodeId !== hit.nodeId;
    if (!entered && text === this.displayText) return;

    this.contactNodeId = hit.nodeId;
    this.paintDisplay(text);
    if (entered) this.pulseOwnerHaptic();
  }

  /**
   * Session start/end: drop without snap-lock. Docked (not held) meter stays.
   * Display returns to idle — do not restore to rest here.
   */
  releaseSessionHolds(): void {
    this.grabs.reset();
    this.onNotProbing();
  }

  /** NEW FAULT / KeyR: park on the dock and clear the readout. */
  restoreToRest(): void {
    this.grabs.restoreAll();
    lockIntoTarget(this.grabbable, this.restTarget);
    this.onNotProbing();
  }

  dispose(): void {
    this.grabs.dispose();
  }

  private nearestContact(): TestContact | null {
    this.probeTip.getWorldPosition(this._probe);
    let best: TestContact | null = null;
    let bestDist = PROBE_CONTACT_RADIUS;
    for (const contact of this.testContacts) {
      contact.object.getWorldPosition(this._jack);
      const d = this._probe.distanceTo(this._jack);
      if (d < bestDist) {
        bestDist = d;
        best = contact;
      }
    }
    return best;
  }

  private onNotProbing(): void {
    if (this.contactNodeId === null && this.displayText === IDLE_DISPLAY) return;
    this.contactNodeId = null;
    this.paintDisplay(IDLE_DISPLAY);
  }

  private paintDisplay(text: string): void {
    if (text === this.displayText) return;
    this.displayText = text;

    const { displayCtx: ctx, displayCanvas: canvas, displayTexture } = this;
    ctx.fillStyle = "#07140c";
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.fillStyle = text === IDLE_DISPLAY ? "#3d7a4a" : "#6fdf88";
    ctx.font = "700 56px ui-monospace, SFMono-Regular, Menlo, monospace";
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText(text, canvas.width / 2, canvas.height / 2 + 4);
    displayTexture.needsUpdate = true;
  }

  private pulseOwnerHaptic(): void {
    const owner = this.grabs.ownerOf(this.grabbable);
    if (typeof owner !== "number") return;
    const controller = this.interaction.getControllers()[owner];
    if (controller) pulseHaptic(this.rig, controller, 0.22, 22);
  }
}

function collectTestContacts(env: WorkshopEnvironment): TestContact[] {
  const contacts: TestContact[] = [];
  for (const [nodeId, object] of Object.entries(env.testPoints)) {
    contacts.push({ nodeId, object });
  }
  for (const name of ["test-switch-line", "test-switch-load"] as const) {
    const object = env.lightBSwitch.getObjectByName(name);
    if (!object) continue;
    const nodeId =
      typeof object.userData.nodeId === "string"
        ? object.userData.nodeId
        : name === "test-switch-load"
          ? SWITCH_LOAD_TEST_NODE_ID
          : "outLightB";
    contacts.push({ nodeId, object });
  }
  return contacts;
}

function relativeLocalPose(
  parent: THREE.Object3D,
  child: THREE.Object3D,
): { position: THREE.Vector3; quaternion: THREE.Quaternion } {
  const inv = new THREE.Matrix4().copy(parent.matrixWorld).invert();
  const local = inv.multiply(child.matrixWorld);
  const position = new THREE.Vector3();
  const quaternion = new THREE.Quaternion();
  local.decompose(position, quaternion, new THREE.Vector3());
  return { position, quaternion };
}

/**
 * Clone standard materials so grab hover / dock pulses stay on this object.
 * Room kit shares steel/copper; pulsing the shared mat would flash terminals.
 */
function uniquifyStandardMaterials(object: THREE.Object3D): void {
  object.traverse((node) => {
    const mesh = node as THREE.Mesh;
    if (!mesh.isMesh) return;
    const mats = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
    const next = mats.map((m) =>
      m instanceof THREE.MeshStandardMaterial ? m.clone() : m,
    );
    mesh.material = Array.isArray(mesh.material) ? next : next[0]!;
  });
}

function pulseHaptic(
  rig: XRRig,
  controller: ControllerHandle,
  intensity: number,
  durationMs: number,
): void {
  const session = rig.renderer.xr.getSession();
  if (!session) return;
  for (const source of session.inputSources) {
    if (source.targetRaySpace == null) continue;
    const gamepad = source.gamepad as Gamepad & {
      hapticActuators?: ReadonlyArray<{
        pulse(value: number, duration: number): Promise<boolean>;
      }>;
    } | null;
    const actuator = gamepad?.hapticActuators?.[0];
    if (actuator?.pulse) {
      void actuator.pulse(intensity, durationMs);
      return;
    }
    const vibe = (
      gamepad as Gamepad & {
        vibrationActuator?: {
          playEffect: (type: string, opts: object) => Promise<unknown>;
        };
      }
    )?.vibrationActuator;
    if (vibe?.playEffect) {
      void vibe.playEffect("dual-rumble", {
        duration: durationMs,
        strongMagnitude: intensity,
        weakMagnitude: intensity * 0.6,
      });
      return;
    }
  }
  void controller;
}
